import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageShell, { SectionLabel } from "@/components/PageShell";
import SEO from "@/components/SEO";
import { submitContact } from "@/lib/api";

const INITIAL = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("All fields are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell testId="page-contact">
      <SEO
        title="ContactUs — Imaginepedia Technologies"
        description="Reach out to us."
        path="/blogs"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 py-16 md:py-24">
          <div className="md:col-span-4">
            <SectionLabel index={1}>Contact</SectionLabel>
          </div>
          <div className="md:col-span-8">
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight">
              Let&apos;s build<br />
              <span className="text-[#CCFF00]">the future.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-400 leading-relaxed max-w-2xl">
              Tell us what you&apos;re imagining. We reply to every well-scoped message within two business days.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 border-t border-white/5 pt-16 pb-24">
          {/* LEFT: coordinates */}
          <div className="md:col-span-5 space-y-10">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Studio
              </p>
              <p className="text-white font-display text-xl">Agartala, Tripura · Remote-first</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Email
              </p>
              <p className="text-white font-mono">contact@imaginepedia.tech</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Response time
              </p>
              <p className="text-white font-mono">&lt; 48h business hours</p>
            </div>
            <div className="pt-6 border-t border-white/5">
              <p className="text-sm text-zinc-400 leading-relaxed max-w-md">
                Prefer async? Include the outline of the problem, timeline, and team you&apos;d be embedding with — we&apos;ll route it to the right lead.
              </p>
            </div>
          </div>

          {/* RIGHT: form */}
          <motion.form
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="md:col-span-7 bg-[#0A0A0B] border border-white/10 p-8 md:p-10"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CCFF00] mb-8">
              &gt; new_message.init()
            </p>
            <div className="space-y-6">
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                testId="contact-name"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                testId="contact-email"
              />
              <Field
                label="Subject"
                name="subject"
                value={form.subject}
                onChange={onChange}
                testId="contact-subject"
              />
              <Field
                label="Message"
                name="message"
                value={form.message}
                onChange={onChange}
                textarea
                testId="contact-message"
              />
              <div className="flex items-center justify-between pt-4">
                <p className="font-mono text-[10px] text-zinc-500">
                  {form.message.length}/5000
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="group inline-flex items-center gap-3 bg-[#CCFF00] text-black px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {submitting ? "Sending…" : "Send message"}
                  <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </PageShell>
  );
}

function Field({ label, name, value, onChange, type = "text", textarea = false, testId }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">
        {label}
      </span>
      {textarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          data-testid={testId}
          rows={5}
          className="w-full bg-[#050506] border border-white/10 text-white p-4 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-colors duration-200 font-mono text-sm resize-none"
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          data-testid={testId}
          className="w-full bg-[#050506] border border-white/10 text-white p-4 focus:outline-none focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] transition-colors duration-200 font-mono text-sm"
        />
      )}
    </label>
  );
}
